import { localize } from '../i18n';
import { translationKey } from '../i18n';
import useNavigate from '../hooks/useSafeNavigate';
import SingleLineText from '../components/ui/InternationalText';
import Button from '../components/ui/FitButton';
import { journey } from '../utils/productJourneys';
import LoadingLabel from '../components/LoadingLabel';
import React, { useState, useRef } from 'react';

import { Box, Typography } from '@mui/material';
import NotificationsActiveIcon from '@mui/icons-material/NotificationsActive';

import FamilyRestroomIcon from '@mui/icons-material/FamilyRestroom';

import HealthAndSafetyIcon from '@mui/icons-material/HealthAndSafety';
import SmartToyIcon from '@mui/icons-material/SmartToy';
import useMedicationAddGate from '../hooks/useMedicationAddGate';
import useAppLanguage from '../i18n';

const SLIDES = [
    {
        icon: <NotificationsActiveIcon sx={{ fontSize: 56, color: '#5a4a00' }} />,
        bg: '#ffe566',
        title: translationKey('먹을 때까지\n절대 안 멈춰요'),
        desc: translationKey('복용 처리할 때까지 집요하게 다시 알려드려요\n무음 모드에서도 벨소리가 울릴 수 있어요'),


    },
    {
        icon: <SmartToyIcon sx={{ fontSize: 56, color: '#6366f1' }} />,
        bg: '#e0e7ff',
        title: translationKey('AI가 약 등록도,\n상담도 해줘요'),
        desc: translationKey('약 이름만 입력하면 복용 정보를 자동 설정하고\n부작용, 복용법 등 궁금한 점도 상담해줘요'),


    },
    {
        icon: <HealthAndSafetyIcon sx={{ fontSize: 56, color: '#dc2626' }} />,
        bg: '#fef2f2',
        title: translationKey('위험한 약 조합,\n미리 경고해줘요'),
        desc: translationKey('약을 등록하면 다른 약과의 상호작용을\n자동으로 체크하고 위험하면 경고해요'),


    },
    {
        icon: <FamilyRestroomIcon sx={{ fontSize: 56, color: '#15803d' }} />,
        bg: '#bbf7d0',
        title: translationKey('가족이 약을 안 먹으면\n보호자에게 알려줘요'),
        desc: translationKey('보호자를 연결하면 복용 현황을 확인하고\n미복용 시 알림을 받을 수 있어요'),


    },
    {
        icon: <NotificationsActiveIcon sx={{ fontSize: 56, color: '#3b82f6' }} />,
        bg: '#dbeafe',
        title: translationKey('알림 권한을\n허용해 주세요'),
        desc: translationKey('복약·가족 알림을 받으려면\n알림 권한이 필요해요'),
        isPermission: true,
    },
];

export default function OnboardingPage() {
    const { language, t } = useAppLanguage();
    const navigate = useNavigate();
    const { openMedicationAdd, MedicationAddGateDialog, checkingMedicationLimit } = useMedicationAddGate();
    const [current, setCurrent] = useState(0);
    const [direction, setDirection] = useState('forward');
    const touchStartX = useRef(0);
    const skippedIntroduction = useRef(false);

    const finish = (requestPermission) => {
        if (requestPermission && window.ReactNativeWebView) {
            window.ReactNativeWebView.postMessage(JSON.stringify({ type: 'REQUEST_NOTIFICATION_PERMISSION' }));
        }
        journey('onboarding', requestPermission ? 'complete' : 'cancel');
        localStorage.setItem('onboarding_done', 'true');
        if (!requestPermission || skippedIntroduction.current) navigate('/');
        else openMedicationAdd('/medications/new-bundle?mode=manual');
    };

    const handleNext = () => {
        if (current < SLIDES.length - 1) {
            setDirection('forward');
            setCurrent(current + 1);
        } else {
            finish(true);
        }
    };

    const handleBack = () => {
        if (current > 0) {
            setDirection('back');
            setCurrent(current - 1);
        }
    };

    const handleTouchStart = (e) => { touchStartX.current = e.touches[0].clientX; };
    const handleTouchEnd = (e) => {
        const diff = touchStartX.current - e.changedTouches[0].clientX;
        if (Math.abs(diff) < 50) return;
        if (diff > 0) {
            if (current < SLIDES.length - 1) handleNext();
        }
        else handleBack();
    };

    const handleSkip = () => {
        skippedIntroduction.current = true;
        setDirection('forward');
        setCurrent(SLIDES.length - 1);
    };

    const slide = SLIDES[current];
    const [title, description] = [t(slide.title), t(slide.desc)];
    const DescriptionLine = language === 'ko' ? SingleLineText : Typography;
    const isLast = current === SLIDES.length - 1;

    return (
        <Box sx={{
            minHeight: '100vh', display: 'flex', flexDirection: 'column',
            bgcolor: '#fff',
        }}>
            {/* Skip */}
            <Box sx={{
                pt: 'calc(env(safe-area-inset-top, 0px) + 16px)',
                px: 2, display: 'flex', justifyContent: 'flex-end',
            }}>
                {!isLast && (
                    <SingleLineText
                        role="button"
                        tabIndex={0}
                        onClick={handleSkip}
                        sx={{ fontSize: '1.05rem', color: '#999', cursor: 'pointer', p: 1 }}
                    >
                        {t('건너뛰기')}
                    </SingleLineText>
                )}
            </Box>

            {/* Content */}
            <Box
                onTouchStart={handleTouchStart}
                onTouchEnd={handleTouchEnd}
                sx={{
                    flex: 1, display: 'flex', flexDirection: 'column',
                    alignItems: 'center', justifyContent: 'center', px: 4,
                    overflow: 'hidden',
                }}
            >
                <Box
                    key={current}
                    sx={{
                        animation: direction === 'forward'
                            ? 'onboardSlideLeft 0.3s ease-out'
                            : 'onboardSlideRight 0.3s ease-out',
                        '@keyframes onboardSlideLeft': {
                            from: { opacity: 0, transform: 'translateX(40px)' },
                            to: { opacity: 1, transform: 'translateX(0)' },
                        },
                        '@keyframes onboardSlideRight': {
                            from: { opacity: 0, transform: 'translateX(-40px)' },
                            to: { opacity: 1, transform: 'translateX(0)' },
                        },
                        display: 'flex', flexDirection: 'column', alignItems: 'center', maxWidth: '100%',
                    }}
                >
                    <Box sx={{
                        width: 120, height: 120, borderRadius: '50%',
                        bgcolor: slide.bg, display: 'flex',
                        alignItems: 'center', justifyContent: 'center', mb: 5,
                    }}>
                        {slide.icon}
                    </Box>
                    <Typography sx={{
                        fontSize: '1.8rem', fontWeight: 800, color: '#1a1a1a',
                        textAlign: 'center', lineHeight: 1.3, mb: 2, width: '100%',
                    }}>
                        {title.split('\n').map(line => <SingleLineText key={line} component="span" sx={{ display: 'block', font: 'inherit' }}>{line}</SingleLineText>)}
                    </Typography>
                    <Typography sx={{
                        fontSize: '1rem', color: '#888', textAlign: 'center',
                        lineHeight: 1.6, width: '100%',
                    }}>
                        {description.split('\n').map(line => <DescriptionLine key={line} component="span" sx={{ display: 'block', font: 'inherit', ...(language === 'ko' ? {} : { lineBreak: 'strict', wordBreak: 'normal', overflowWrap: 'normal' }) }}>{line}</DescriptionLine>)}
                    </Typography>
                </Box>
            </Box>

            {/* Dots + CTA */}
            <Box sx={{
                px: 3, pb: 'calc(env(safe-area-inset-bottom, 0px) + 24px)',
            }}>
                {/* Dots */}
                <Box sx={{ display: 'flex', justifyContent: 'center', gap: 1, mb: 3 }}>
                    {SLIDES.map((_, i) => (
                        <Box key={i} role="button" tabIndex={0} aria-label={`${t('슬라이드')} ${i + 1}`} onClick={() => { setDirection(i > current ? 'forward' : 'back'); setCurrent(i); }} sx={{
                            width: i === current ? 24 : 8, height: 8, borderRadius: 4,
                            bgcolor: i === current ? '#5a4a00' : '#e5e7eb',
                            transition: 'all 0.25s ease', cursor: 'pointer',
                        }} />
                    ))}
                </Box>

                <Button
                    fullWidth variant="contained"
                    onMouseDown={(e) => e.preventDefault()}
                    disabled={checkingMedicationLimit}
                    onClick={handleNext}
                    sx={{
                        bgcolor: '#ffe566', color: '#5a4a00', borderRadius: 3,
                        py: 1.8, fontSize: '1.1rem', fontWeight: 700,
                        boxShadow: '0 4px 12px rgba(230,168,0,0.3)',
                        '&:hover': { bgcolor: '#e8d040' },
                    }}
                >
                    {checkingMedicationLimit ? <LoadingLabel /> : slide.isPermission ? localize('알림 권한 요청하고 계속하기') : isLast ? localize('약 등록하러 가기') : localize('다음')}
                </Button>
                {slide.isPermission && (
                    <Button fullWidth variant="text" disabled={checkingMedicationLimit}
                        onClick={() => finish(false)}
                        sx={{ mt: 1, minHeight: 44, color: '#777', borderRadius: 3 }}>
                        {t('나중에')}
                    </Button>
                )}
            </Box>
            <MedicationAddGateDialog />
        </Box>
    );
}
